import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FiEdit3, FiEye } from "react-icons/fi"

import useT from "utils/hooks/useT"
import Loading from "components/Loading"
import PendingApprovalLink from "components/Lims/ChangeControlLink"
import { limsSpecs } from "state_management"
import { selectCanAuthorMasterData } from "state_management/permissions"
import AnalysisSpecificationEditor from "./AnalysisSpecificationEditor"

// LIMS › Analysis specifications (SPEC-063, Plannertech-user#4149) — where the parameter
// specifications are created and seen.
//
// The specification used to be edited inside the analysis definition under Setup, next to the
// colour, the tasks and the performers. Two things made that wrong. It is a QC METHOD, not
// planning setup — and Setup is planner-only, so the reviewers and supervisors who must approve a
// specification could not reach the page where it lived. This register is open to every LIMS user
// (SPEC-022 doctrine: everyone can SEE); AUTHORING is planner-only, as for every other master-data
// register (selectCanAuthorMasterData — the server 403s anyone else regardless).
//
// What did NOT move: the consumable requirements. SPEC-063 split them out of the specification —
// they are ordinary analysis data now, unversioned and unsigned, and they stay on the definition
// page. This screen is the parameter specification and nothing else.
//
// Change control is unchanged: saving a CHANGED specification creates a draft version, and the
// specification result entry judges against stays in force until a reviewer and then a supervisor
// e-sign it in LIMS › Change control. The status column is the honest report of that — "in force"
// is not the same as "what you last typed", and the register says which is which.
export default function AnalysisSpecifications() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const rows = useSelector(limsSpecs.selectSpecList)
  const isLoading = useSelector((state) => state.limsSpecs.isLoading)
  const canAuthor = useSelector(selectCanAuthorMasterData)

  // null = the list; an id = that analysis's specification, open.
  const [editingId, setEditingId] = useState(null)

  useEffect(() => {
    dispatch(limsSpecs.fetch())
  }, [dispatch])

  return (
    <div className="LimsAnalysisSpecs">
      <div className="LimsAnalysisSpecs-header">
        <h1 className="LimsAnalysisSpecs-title">{t("limsAnalysisSpecsTitle")}</h1>
      </div>
      <p className="LimsAnalysisSpecs-intro">{t("limsAnalysisSpecsIntro")}</p>

      {isLoading && rows.length === 0 && <Loading />}
      {!isLoading && rows.length === 0 && (
        <p className="LimsAnalysisSpecs-empty">{t("limsAnalysisSpecsNone")}</p>
      )}
      {rows.length > 0 && (
        <Table striped hover responsive className="LimsAnalysisSpecs-table">
          <thead>
            <tr>
              <th>{t("limsAnalysisSpecsAnalysis")}</th>
              <th>{t("limsAnalysisSpecsParameters")}</th>
              <th>{t("limsAnalysisSpecsVersion")}</th>
              <th>{t("limsAnalysisSpecsStatus")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.analysisDefinitionId}>
                <td>{row.name}</td>
                <td>{row.parameterCount}</td>
                <td>{row.effectiveVersionNumber ? `v${row.effectiveVersionNumber}` : "—"}</td>
                <td>
                  <span className={"LimsAnalysisSpecs-status " + statusClass(row)}>{t(statusLabelKey(row))}</span>
                </td>
                <td className="LimsAnalysisSpecs-actions">
                  {/* A pending version points at Change control, where it is actually signed —
                      the same pointer every other master-data register shows (SPEC-058 B). */}
                  <PendingApprovalLink state={row.pendingVersionState} itemId={row.pendingVersionId} />
                  <Button variant="link" size="sm" onClick={() => setEditingId(row.analysisDefinitionId)}>
                    {canAuthor ? <FiEdit3 /> : <FiEye />}{" "}
                    {t(canAuthor ? "limsAnalysisSpecOpen" : "limsAnalysisSpecView")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* The specification opens in a MODAL over the register, the same way every other LIMS
          register edits one of its records (consumables, SST templates, comment library). It used
          to REPLACE the register with a full screen, which is the one pattern this app does not
          use for editing a row. */}
      {editingId && (
        <AnalysisSpecificationEditor
          analysisDefinitionId={editingId}
          canAuthor={canAuthor}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  )
}

// The status of ONE analysis's specification, in the order a reader cares about: something waiting
// to be approved is the most important thing to say, then whether there is a specification at all.
function statusLabelKey(row) {
  if (row.pendingVersionState === "reviewer-approved") return "limsAnalysisSpecStatusPendingSupervisor"
  if (row.pendingVersionState === "draft") return "limsAnalysisSpecStatusPendingReviewer"
  if (row.effectiveVersionNumber) return "limsAnalysisSpecStatusEffective"
  return "limsAnalysisSpecStatusNone"
}

function statusClass(row) {
  if (row.pendingVersionState === "reviewer-approved") return "is-reviewer"
  if (row.pendingVersionState === "draft") return "is-draft"
  if (row.effectiveVersionNumber) return "is-effective"
  return "is-none"
}
